import { useEffect, useState } from 'react';

interface Props {
  current: number | null;
  onSave: (value: number | null) => Promise<void> | void;
  onClose: () => void;
}

export function TargetModal({ current, onSave, onClose }: Props) {
  const [value, setValue] = useState(current != null ? String(current) : '');
  const [submitting, setSubmitting] = useState(false);

  // Bloquer le scroll de la page en fond
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Échap pour fermer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSave() {
    const parsed = parseFloat(value.replace(',', '.').replace(/\s/g, ''));
    const final = isNaN(parsed) || parsed <= 0 ? null : parsed;
    setSubmitting(true);
    try {
      await onSave(final);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove() {
    setSubmitting(true);
    try {
      await onSave(null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="target-modal-title"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 shadow-xl"
        onClick={e => e.stopPropagation()}
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
      >
        <div className="text-center mb-4">
          <div className="text-3xl mb-1">🎯</div>
          <h2 id="target-modal-title" className="text-lg font-demi text-ink">
            Objectif mensuel
          </h2>
          <p className="text-xs text-ink-sub mt-1">
            Définis ton objectif de commission pour le mois.
            Tu pourras le modifier ou le retirer à tout moment.
          </p>
        </div>

        <label className="block">
          <span className="block text-sm font-demi text-ink-sub mb-1.5">
            Montant cible (€)
          </span>
          <input
            type="number"
            min="0"
            step="100"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="Ex : 5 000"
            className="input"
            autoFocus
          />
        </label>

        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting}
            className="w-full py-3 rounded-xl bg-teal text-white font-demi hover:bg-teal-dark transition-colors disabled:opacity-60"
          >
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </button>

          {current && current > 0 && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={submitting}
              className="w-full py-2.5 rounded-xl text-ink-sub hover:text-red-500 text-sm transition-colors"
            >
              Retirer l'objectif
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="w-full py-2.5 rounded-xl text-ink-sub hover:text-ink text-sm transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
